import { c as _c } from "react/compiler-runtime";
import { IcoCopy16, IcoTrash16 } from '@onefootprint/icons';
import { IconButton, Tooltip, useConfirmationDialog } from '@onefootprint/ui';
import snakeCase from 'lodash/snakeCase';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FieldKeyErrorMessage from '../../components/field-key-error';
import { InlineText } from '../../components/shared/inline-edit';
import { RulesMetaSuggestions, RulesNodeSuggestions } from '../../components/suggestion/rules-suggestions';
import ValidationAlerts from '../../components/validation-alerts';
import { useNodeSave } from '../../hooks/use-node-save';
import { fieldKeyError } from '../../store/field-key';
import { useBuilder } from '../../store/provider';
import type { RulesNode } from '../../store/types';
import { DocumentPane } from '../document-pane';
import TitleEditor from '../title-editor';
import RulesCode from './rules-code';
type RulesPaneProps = {
  rulesNode: RulesNode;
  /** 1-based position in the plan, matching the number the nav shows for this node. */
  position: number;
};
const RulesPane = t0 => {
  const $ = _c(76);
  const {
    rulesNode,
    position
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.rules"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const canEdit = !isReadOnly;
  const confirm = useConfirmationDialog();
  let t2;
  if ($[1] !== rulesNode.key || $[2] !== rulesNode.name) {
    t2 = !!rulesNode.key && rulesNode.key !== snakeCase(rulesNode.name);
    $[1] = rulesNode.key;
    $[2] = rulesNode.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(t2);
  const save = useNodeSave(rulesNode.id);
  let t3;
  if ($[4] !== actions || $[5] !== confirm || $[6] !== rulesNode.id || $[7] !== rulesNode.name || $[8] !== t) {
    t3 = () => {
      confirm.open({
        title: t(_temp),
        description: t(_temp2, {
          name: rulesNode.name || t(_temp3)
        }),
        variant: "destructive",
        primaryButton: {
          label: t(_temp4),
          onClick: () => actions.removeNode(rulesNode.id)
        },
        secondaryButton: {
          label: t(_temp5)
        }
      });
    };
    $[4] = actions;
    $[5] = confirm;
    $[6] = rulesNode.id;
    $[7] = rulesNode.name;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleDelete = t3;
  let t4;
  if ($[10] !== actions.rules || $[11] !== keyManuallyEdited || $[12] !== rulesNode.id) {
    t4 = next => {
      const patch = keyManuallyEdited ? {
        name: next
      } : {
        name: next,
        key: snakeCase(next)
      };
      actions.rules.updateNode(rulesNode.id, patch);
    };
    $[10] = actions.rules;
    $[11] = keyManuallyEdited;
    $[12] = rulesNode.id;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleNameChange = t4;
  let t5;
  if ($[14] !== actions.rules || $[15] !== rulesNode.id) {
    t5 = next_0 => {
      setKeyManuallyEdited(true);
      actions.rules.updateNode(rulesNode.id, {
        key: next_0
      });
    };
    $[14] = actions.rules;
    $[15] = rulesNode.id;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const handleKeyChange = t5;
  let t6;
  if ($[17] !== rulesNode.key) {
    t6 = fieldKeyError(rulesNode.key);
    $[17] = rulesNode.key;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const keyError = t6;
  const t7 = `rules-${rulesNode.id}`;
  let t8;
  if ($[19] !== position || $[20] !== t) {
    t8 = t(_temp6, {
      position
    });
    $[19] = position;
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = rulesNode.name;
  let t10;
  if ($[22] !== t) {
    t10 = t(_temp7);
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t(_temp8);
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = state.focusNodeId === rulesNode.id;
  let t13;
  if ($[26] !== canEdit || $[27] !== handleNameChange || $[28] !== rulesNode.name || $[29] !== t10 || $[30] !== t11 || $[31] !== t12) {
    t13 = <TitleEditor value={t9} placeholder={t10} ariaLabel={t11} editable={canEdit} autoFocus={t12} onChange={handleNameChange} />;
    $[26] = canEdit;
    $[27] = handleNameChange;
    $[28] = rulesNode.name;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== actions || $[34] !== canEdit || $[35] !== handleDelete || $[36] !== rulesNode || $[37] !== t) {
    t14 = canEdit && <><Tooltip content={t(_temp9)}><IconButton aria-label={t(_temp10)} icon={IcoCopy16} size="compact" variant="ghost" onClick={() => actions.copyNode(rulesNode)} /></Tooltip><Tooltip content={t(_temp11)}><IconButton aria-label={t(_temp12)} icon={IcoTrash16} size="compact" variant="ghost" onClick={handleDelete} /></Tooltip></>;
    $[33] = actions;
    $[34] = canEdit;
    $[35] = handleDelete;
    $[36] = rulesNode;
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== rulesNode.id) {
    t15 = <ValidationAlerts nodeId={rulesNode.id} />;
    $[39] = rulesNode.id;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== rulesNode) {
    t16 = <RulesMetaSuggestions rulesNode={rulesNode} />;
    $[41] = rulesNode;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const t17 = rulesNode.key;
  let t18;
  if ($[43] !== t) {
    t18 = t(_temp13);
    $[43] = t;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t) {
    t19 = t(_temp14);
    $[45] = t;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  const t20 = !!keyError;
  let t21;
  if ($[47] !== canEdit || $[48] !== handleKeyChange || $[49] !== rulesNode.key || $[50] !== t18 || $[51] !== t19 || $[52] !== t20) {
    t21 = <InlineText value={t17} prefix="outputs." mono={true} ariaLabel={t18} placeholder={t19} canEdit={canEdit} hasError={t20} onChange={handleKeyChange} />;
    $[47] = canEdit;
    $[48] = handleKeyChange;
    $[49] = rulesNode.key;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== keyError) {
    t22 = <FieldKeyErrorMessage error={keyError} />;
    $[54] = keyError;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t) {
    t23 = t(_temp15);
    $[56] = t;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t23) {
    t24 = <p className="text-body-2 text-tertiary">{t23}</p>;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  let t26;
  if ($[60] !== rulesNode) {
    t25 = <RulesCode rulesNode={rulesNode} />;
    t26 = <RulesNodeSuggestions rulesNode={rulesNode} />;
    $[60] = rulesNode;
    $[61] = t25;
    $[62] = t26;
  } else {
    t25 = $[61];
    t26 = $[62];
  }
  let t27;
  if ($[63] !== save || $[64] !== t13 || $[65] !== t14 || $[66] !== t15 || $[67] !== t16 || $[68] !== t21 || $[69] !== t22 || $[70] !== t24 || $[71] !== t25 || $[72] !== t26 || $[73] !== t7 || $[74] !== t8) {
    t27 = <DocumentPane onBlur={save} id={t7} eyebrow={t8} title={t13} contentClassName="gap-4" actions={t14}>{t15}{t16}{t21}{t22}{t24}{t25}{t26}</DocumentPane>;
    $[63] = save;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
    $[67] = t16;
    $[68] = t21;
    $[69] = t22;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
    $[73] = t7;
    $[74] = t8;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  return t27;
};
export default RulesPane;
function _temp($) {
  return $.delete.title;
}
function _temp2($_0) {
  return $_0.delete.description;
}
function _temp3($_1) {
  return $_1.newRulesNode;
}
function _temp4($_2) {
  return $_2.delete.confirm;
}
function _temp5($_3) {
  return $_3.delete.cancel;
}
function _temp6($_4) {
  return $_4.eyebrow;
}
function _temp7($_5) {
  return $_5.newRulesNode;
}
function _temp8($_6) {
  return $_6.name.label;
}
function _temp9($_7) {
  return $_7.copy.ariaLabel;
}
function _temp10($_8) {
  return $_8.copy.ariaLabel;
}
function _temp11($_9) {
  return $_9.delete.ariaLabel;
}
function _temp12($_10) {
  return $_10.delete.ariaLabel;
}
function _temp13($_11) {
  return $_11.outputKey.label;
}
function _temp14($_12) {
  return $_12.outputKey.placeholder;
}
function _temp15($_13) {
  return $_13.intro;
}